import React, { useState, useEffect } from 'react';
import { Agent, CreateAgentRequest, UpdateAgentRequest, LLM_PROVIDERS, LLMProvider } from '../types/agent';
import { Save, X, Zap, DollarSign } from 'lucide-react';

interface AgentFormProps {
  agent?: Agent;
  prompts: any[];
  onSubmit: (data: CreateAgentRequest | UpdateAgentRequest) => Promise<void>;
  onCancel: () => void;
  loading: boolean;
}

const AgentForm: React.FC<AgentFormProps> = ({ agent, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    name: agent?.name || '',
    description: agent?.description || '',
    systemPromptId: agent?.systemPromptId || 1,
    conversationPromptId: agent?.conversationPromptId || 2,
    llmProvider: (agent?.llmProvider || 'qwen') as LLMProvider,
    llmModel: agent?.llmModel || 'qwen-turbo',
    isActive: agent?.isActive ?? true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const currentProvider = LLM_PROVIDERS.find(p => p.id === formData.llmProvider);
  const availableModels = currentProvider?.models || [];
  const currentModel = availableModels.find(m => m.id === formData.llmModel);

  useEffect(() => {
    if (agent) {
      setFormData({
        name: agent.name,
        description: agent.description,
        systemPromptId: agent.systemPromptId,
        conversationPromptId: agent.conversationPromptId,
        llmProvider: agent.llmProvider,
        llmModel: agent.llmModel,
        isActive: agent.isActive
      });
    }
  }, [agent]);

  useEffect(() => {
    if (!availableModels.find(m => m.id === formData.llmModel)) {
      setFormData(prev => ({
        ...prev,
        llmModel: availableModels[0]?.id || ''
      }));
    }
  }, [formData.llmProvider, availableModels]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    } else if (formData.name.length < 3) {
      newErrors.name = 'El nombre debe tener al menos 3 caracteres';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'La descripción es requerida';
    } else if (formData.description.length < 10) {
      newErrors.description = 'La descripción debe tener al menos 10 caracteres';
    }

    if (!formData.systemPromptId || formData.systemPromptId < 1) {
      newErrors.systemPromptId = 'El ID del prompt de sistema debe ser mayor a 0';
    }

    if (!formData.conversationPromptId || formData.conversationPromptId < 1) {
      newErrors.conversationPromptId = 'El ID del prompt de conversación debe ser mayor a 0';
    }

    if (!formData.llmProvider) {
      newErrors.llmProvider = 'Debe seleccionar un proveedor LLM';
    }

    if (!formData.llmModel) {
      newErrors.llmModel = 'Debe seleccionar un modelo';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProviderChange = (provider: LLMProvider) => {
    const providerConfig = LLM_PROVIDERS.find(p => p.id === provider);
    const firstModel = providerConfig?.models[0]?.id || '';
    
    setFormData(prev => ({
      ...prev,
      llmProvider: provider,
      llmModel: firstModel
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      if (agent) {
        await onSubmit({
          id: agent.id,
          ...formData
        });
      } else {
        await onSubmit(formData);
      }
    } catch (error) {
      console.error('Error saving agent:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="agent-form" onSubmit={handleSubmit}>
      <h3>{agent ? 'Editar Agente' : 'Crear Nuevo Agente'}</h3>

      <div className="form-group">
        <label htmlFor="name">Nombre del Agente *</label>
        <input
          id="name"
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="ej: existing_customer"
          className={errors.name ? 'error' : ''}
          disabled={loading || saving}
        />
        {errors.name && <span className="error-message">{errors.name}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="description">Descripción *</label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Describe el propósito de este agente..."
          rows={3}
          className={errors.description ? 'error' : ''}
          disabled={loading || saving}
        />
        {errors.description && <span className="error-message">{errors.description}</span>}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="systemPromptId">ID del Prompt de Sistema *</label>
          <input
            id="systemPromptId"
            type="number"
            value={formData.systemPromptId}
            onChange={(e) => setFormData({ ...formData, systemPromptId: parseInt(e.target.value) || 0 })}
            placeholder="ej: 1"
            min="1"
            className={errors.systemPromptId ? 'error' : ''}
            disabled={loading || saving}
          />
          {errors.systemPromptId && <span className="error-message">{errors.systemPromptId}</span>}
          <small>Normalmente es 1 para el prompt del sistema principal</small>
        </div>

        <div className="form-group">
          <label htmlFor="conversationPromptId">ID del Prompt de Conversación *</label>
          <input
            id="conversationPromptId"
            type="number"
            value={formData.conversationPromptId}
            onChange={(e) => setFormData({ ...formData, conversationPromptId: parseInt(e.target.value) || 0 })}
            placeholder="ej: 2"
            min="1"
            className={errors.conversationPromptId ? 'error' : ''}
            disabled={loading || saving}
          />
          {errors.conversationPromptId && <span className="error-message">{errors.conversationPromptId}</span>}
          <small>ID del prompt que define el comportamiento conversacional</small>
        </div>
      </div>

      <div className="llm-section">
        <h4><Zap size={16} /> Configuración del Modelo LLM</h4>
        
        <div className="form-group">
          <label htmlFor="llmProvider">Proveedor LLM *</label>
          <select
            id="llmProvider"
            value={formData.llmProvider}
            onChange={(e) => handleProviderChange(e.target.value as LLMProvider)}
            className={errors.llmProvider ? 'error' : ''}
            disabled={loading || saving}
          >
            {LLM_PROVIDERS.map(provider => (
              <option key={provider.id} value={provider.id}>
                {provider.name}
              </option>
            ))}
          </select>
          {errors.llmProvider && <span className="error-message">{errors.llmProvider}</span>}
          {currentProvider && (
            <small>{currentProvider.description}</small>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="llmModel">Modelo *</label>
          <select
            id="llmModel"
            value={formData.llmModel}
            onChange={(e) => setFormData({ ...formData, llmModel: e.target.value })}
            className={errors.llmModel ? 'error' : ''}
            disabled={loading || saving || availableModels.length === 0}
          >
            {availableModels.map(model => (
              <option key={model.id} value={model.id}>
                {model.name}
              </option>
            ))}
          </select>
          {errors.llmModel && <span className="error-message">{errors.llmModel}</span>}
          {currentModel && (
            <div className="model-info">
              <small>{currentModel.description}</small>
              {currentModel.pricing && (
                <small className="pricing-info">
                  <DollarSign size={12} />
                  ${currentModel.pricing.input}/${currentModel.pricing.output} per {currentModel.pricing.unit}
                </small>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="form-group checkbox">
        <label>
          <input
            type="checkbox"
            checked={formData.isActive}
            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
            disabled={loading || saving}
          />
          Agente activo
        </label>
      </div>

      <div className="form-actions">
        <button
          type="button"
          className="btn secondary"
          onClick={onCancel}
          disabled={loading || saving}
        >
          <X size={16} />
          Cancelar
        </button>
        <button
          type="submit"
          className="btn primary"
          disabled={loading || saving}
        >
          <Save size={16} />
          {saving ? 'Guardando...' : (agent ? 'Actualizar' : 'Crear')}
        </button>
      </div>

      <style>{`
        .agent-form {
          max-width: 700px;
          margin: 0 auto;
        }

        .agent-form h3 {
          margin-bottom: 20px;
          color: #333;
        }

        .agent-form h4 {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 0 0 15px 0;
          color: #555;
          font-size: 16px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          margin-bottom: 6px;
          font-weight: 500;
          color: #555;
        }

        .form-group input,
        .form-group textarea,
        .form-group select {
          width: 100%;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
          transition: border-color 0.2s;
        }

        .form-group input[type="number"] {
          width: 120px;
        }

        .form-group input:focus,
        .form-group textarea:focus,
        .form-group select:focus {
          outline: none;
          border-color: #4CAF50;
        }

        .form-group input.error,
        .form-group textarea.error,
        .form-group select.error {
          border-color: #f44336;
        }

        .form-group input:disabled,
        .form-group textarea:disabled,
        .form-group select:disabled {
          background: #f5f5f5;
          cursor: not-allowed;
        }

        .form-group.checkbox label {
          display: flex;
          align-items: center;
          cursor: pointer;
        }

        .form-group.checkbox input {
          width: auto;
          margin-right: 8px;
        }

        .form-group small {
          display: block;
          margin-top: 4px;
          color: #666;
          font-size: 12px;
        }

        .error-message {
          display: block;
          margin-top: 4px;
          color: #f44336;
          font-size: 12px;
        }

        .llm-section {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 8px;
          margin: 20px 0;
          border: 1px solid #e9ecef;
        }

        .model-info {
          margin-top: 6px;
        }

        .pricing-info {
          display: flex;
          align-items: center;
          gap: 4px;
          color: #007bff;
          font-weight: 500;
          margin-top: 2px;
        }

        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          margin-top: 30px;
          padding-top: 20px;
          border-top: 1px solid #e0e0e0;
        }

        .btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 20px;
          border: none;
          border-radius: 4px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn.primary {
          background: #4CAF50;
          color: white;
        }

        .btn.primary:hover:not(:disabled) {
          background: #45a049;
        }

        .btn.secondary {
          background: #f5f5f5;
          color: #666;
          border: 1px solid #ddd;
        }

        .btn.secondary:hover:not(:disabled) {
          background: #e0e0e0;
        }

        @media (max-width: 768px) {
          .form-row {
            grid-template-columns: 1fr;
          }
          
          .agent-form {
            max-width: 100%;
            padding: 0 10px;
          }
        }
      `}</style>
    </form>
  );
};

export default AgentForm;